import React, { useState, useEffect } from 'react';
import { Cloud, MonitorPlayIcon, BookOpenCheck } from 'lucide-react';
import BlogSection from './BlogSection';
import OgpPreview from './OgpPreview';
import OptimizedImage from './OptimizedImage';
import { useImagePreloader } from '../hooks/useImagePreloader';
import useOgpCache from '../hooks/useOgpCache';
import { useSocialLinks } from '../data/socialLinks';
import { FEATURED_ARTICLES, OTHER_LINKS } from '../data/articles';

const Portfolio = () => {
  const { socialLinks } = useSocialLinks();
  const [isMobile, setIsMobile] = useState(false);
  const [ogpDataCache, setOgpDataCache] = useState({});
  const { preloadOgpData } = useOgpCache();

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Preload OGP data in parallel
  useEffect(() => {
    const loadOgpData = async () => {
      const urls = FEATURED_ARTICLES.map(article => article.url);
      const results = await preloadOgpData(urls);
      
      const cache = {};
      results.forEach(({ url, data, error }) => {
        if (data && !error) {
          cache[url] = data;
        }
      });
      
      setOgpDataCache(cache);
    };

    loadOgpData();
  }, [preloadOgpData]);
  
  const imageConfig = [
    {
      key: 'background-desktop',
      webp: '/images/Community_Builder_LinkedIn_2048x1152_upperright.webp',
      fallback: '/images/Community_Builder_LinkedIn_2048x1152_upperright.png'
    },
    {
      key: 'background-mobile',
      webp: '/images/Community_Builder_Mobile_1024x536.webp',
      fallback: '/images/Community_Builder_Mobile_1024x536.png'
    },
    {
      key: 'profile',
      webp: '/images/profile.png',
      fallback: '/images/profile.png'
    }
  ];
  
  const { loadedImages, isWebPSupported } = useImagePreloader(imageConfig);

  const getBackgroundImage = () => {
    if (isMobile) {
      return isWebPSupported 
        ? '/images/Community_Builder_Mobile_1024x536.webp'
        : '/images/Community_Builder_Mobile_1024x536.png';
    }
    return isWebPSupported 
      ? '/images/Community_Builder_LinkedIn_2048x1152_upperright.webp'
      : '/images/Community_Builder_LinkedIn_2048x1152_upperright.png';
  };

  const backgroundImage = getBackgroundImage();
  const profileImage = '/images/profile.png';
  
  const isBackgroundLoaded = isMobile 
    ? loadedImages['background-mobile']
    : loadedImages['background-desktop'];
  
  const headerStyle = {
    background: isBackgroundLoaded 
      ? `url('${backgroundImage}') top center / cover no-repeat, linear-gradient(to right, #2563EB, #1E40AF)`
      : 'linear-gradient(to right, #2563EB, #1E40AF)',
    height: '425px',
    minHeight: '425px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    transition: 'background 0.3s ease-in-out',
    willChange: 'background'
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header/Hero Section */}
      <header
        style={headerStyle}
        className="text-white"
        role="banner"
        aria-label="Portfolio header"
      >
        {!isBackgroundLoaded && (
          <div 
            className="absolute inset-0 bg-gradient-to-r from-blue-600 to-blue-800 animate-pulse"
            style={{ zIndex: 1 }}
          />
        )}
        <div className="max-w-4xl mx-auto px-4" style={{ position: 'relative', zIndex: 2 }}>
          <div className="flex flex-col items-center mb-8">
            <div className="w-40 h-40 rounded-full overflow-hidden bg-white mb-6 border-4 border-white shadow-lg relative">
              {!loadedImages.profile && (
                <div className="w-full h-full bg-gray-200 animate-pulse" />
              )}
              <OptimizedImage
                src={profileImage}
                webpSrc={profileImage}
                alt="Tsuyoshi Yamasaki profile"
                className="w-full h-full object-cover"
                loading="eager"
                width={160}
                height={160}
              />
            </div>
          </div>
          <div className="text-center">
            {/* モバイル用の半透明背景オーバーレイ - 名前と職業情報を統合 */}
            <div className={`${isMobile ? 'bg-black bg-opacity-50 rounded-lg p-3 mx-4' : ''} mb-4`}>
              <div className="mb-3">
                <h1 className={`${isMobile ? 'text-xl' : 'text-3xl'} font-bold inline ${isMobile ? 'drop-shadow-lg' : ''}`}>山崎 毅</h1>
                <span className={`${isMobile ? 'text-lg' : 'text-xl'} text-blue-50 mx-2`}>/</span>
                <h2 className={`${isMobile ? 'text-lg' : 'text-2xl'} text-blue-50 inline ${isMobile ? 'drop-shadow-lg' : ''}`}>Tsuyoshi Yamasaki</h2>
              </div>
              <h3 className={`${isMobile ? 'text-sm' : 'text-lg'} ${isMobile ? 'drop-shadow-lg' : ''}`}>
                Site Reliability Engineer
                <br />
                AWS Community Builder (AI Engineering)
              </h3>
            </div>
          </div>

          <div className={`flex flex-wrap gap-2 items-center justify-center ${isMobile ? 'bg-black bg-opacity-50 rounded-lg p-3 mx-4' : ''}`}>
            {socialLinks.map((link) => {
              const { id, href, title, icon: Icon, customIcon, customSvg, ariaLabel } = link;
              
              return (
                <a
                  key={id}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`${isMobile ? 'p-1.5' : 'p-2'} hover:bg-blue-700 rounded-full transition-colors ${isMobile ? 'drop-shadow-lg' : ''}`}
                  title={title}
                >
                  {Icon && <Icon aria-label={ariaLabel} size={isMobile ? 20 : 24} />}
                  {customIcon && (
                    <img
                      src={customIcon}
                      alt={title}
                      className={isMobile ? "w-5 h-4" : "w-7 h-5"}
                    />
                  )}
                  {customSvg && (
                    <div className={isMobile ? "w-5 h-5" : "w-6 h-6"}>
                      {React.cloneElement(customSvg, {
                        className: isMobile ? "w-5 h-5" : "w-6 h-6"
                      })}
                    </div>
                  )}
                </a>
              );
            })}
          </div>
        </div>
      </header>

      {/* About Me Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl font-bold mb-8 text-gray-800">About Me</h2>
          <div className="grid grid-cols-1 gap-6">
            {FEATURED_ARTICLES.map((article) => (
              <div key={article.url} className="bg-white border rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                <a
                  href={article.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <OgpPreview 
                    url={article.url} 
                    preloadedData={ogpDataCache[article.url]} 
                  />
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl font-bold mb-8 text-gray-800">Other Links</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {OTHER_LINKS.map((section) => {
              const IconComponent = section.icon === 'Cloud' ? Cloud : 
                                 section.icon === 'BookOpenCheck' ? BookOpenCheck : 
                                 MonitorPlayIcon;
              
              return (
                <div key={section.id} className="p-6 bg-gray-50 rounded-lg">
                  <IconComponent className="w-12 h-12 text-blue-600 mb-4" />
                  
                  {section.links ? (
                    <div className="space-y-3">
                      {section.links.map((link) => (
                        <h3 key={link.title} className="text-xl font-semibold">
                          <a 
                            href={link.url} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="text-blue-600 hover:underline"
                          >
                            {link.title}
                          </a>
                        </h3>
                      ))}
                    </div>
                  ) : (
                    <>
                      <h3 className="text-xl font-semibold mb-2">
                        <a 
                          href={section.url} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="text-blue-600 hover:underline"
                        >
                          {section.title}
                        </a>
                      </h3>
                      <p className="text-gray-600">
                        {section.description}
                        {section.videoUrl && (
                          <p>
                            <a 
                              href={section.videoUrl} 
                              target="_blank" 
                              rel="noopener noreferrer" 
                              className="text-blue-600 hover:underline"
                            >
                              ▶ アーカイブ動画
                            </a>
                          </p>
                        )}
                      </p>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Blog Section */}
      <BlogSection />
    </div>
  );
};

export default Portfolio;
